import React from "react";
import {FormattedMessage, useIntl} from "react-intl";
import {twJoin} from "tailwind-merge";
import {Badge, Button, defaultBorderColor, Icon} from "peggirkit";
import {ArrowDownIcon, ArrowUpIcon, BarsArrowDownIcon, PlusIcon, XMarkIcon} from "@heroicons/react/16/solid";
import {formatMoney} from "@/utils/MoneyUtils";
import {SetStateFn} from "@/utils/SetStateUtils";
import {OptionGroupsLookup} from "@widgets/menuEditor/optionGroups/optionGroupsLookup";
import {
    draftName,
    DraftOption,
    sortDraftsAlphabetically,
} from "@widgets/menuEditor/optionGroups/edit/optionGroupDraft";
import "simplebar-react/dist/simplebar.min.css";
import SimpleBar from "simplebar-react";

type Props = {
    savedOptionIds: number[], // Options of the group as saved (empty for a new group)
    drafts: DraftOption[],
    setDrafts: SetStateFn<DraftOption[]>,
    lookup: OptionGroupsLookup,
    canAdd: boolean,
    onAdd: () => void,
    disabled: boolean,
};

const iconButtonClassName = twJoin(
    "p-1.5 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-900",
    "dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-white",
    "disabled:opacity-30 disabled:pointer-events-none",
);

const move = (drafts: DraftOption[], index: number, offset: -1 | 1): DraftOption[] => {
    const result = [...drafts];
    [result[index], result[index + offset]] = [result[index + offset], result[index]];
    return result;
};

const DraftPrices = ({draft}: { draft: DraftOption }) => {
    const {price, priceTakeaway} = draft.kind === "existing" ? draft.option : draft.values;
    const formatted = (amount: string) => Number(amount) === 0 ? null : formatMoney(Number(amount));

    return (
        <span className={"tabular-nums whitespace-nowrap text-gray-500 dark:text-slate-400"}>
            {formatted(price) ?? <FormattedMessage id={"free"}/>}
            {price !== priceTakeaway && <> · {formatted(priceTakeaway) ?? <FormattedMessage id={"free"}/>}</>}
        </span>
    );
};

/**
 * The options of the group being edited: add, remove, reorder and sort them.
 * Nothing is saved until the group is saved.
 */
const DraftOptionsList = ({savedOptionIds, drafts, setDrafts, lookup, canAdd, onAdd, disabled}: Props) => {
    const intl = useIntl();

    // Other groups an existing option is in (this group not included)
    const otherGroupCount = (draft: DraftOption): number => {
        if (draft.kind === "new") return 0;
        const count = lookup.groupCountByOption.get(draft.option.id) ?? 0;
        return Math.max(0, count - (savedOptionIds.includes(draft.option.id) ? 1 : 0));
    };

    return (
        <section className={"w-full flex flex-col gap-3"}>
            <div className={"w-full flex flex-row flex-wrap items-center justify-between gap-3"}>
                <h3 className={"font-medium text-gray-900 dark:text-white"}>
                    <FormattedMessage id={"options"}/>
                    <span className={"ml-2 font-normal text-gray-500 dark:text-slate-400"}>
                        {drafts.length}
                    </span>
                </h3>

                <div className={"flex flex-row items-center gap-2"}>
                    <Button
                        color={"secondary"}
                        size={"xs"}
                        iconBefore={<Icon icon={BarsArrowDownIcon}/>}
                        disabled={disabled || drafts.length < 2}
                        buttonAction={{
                            as: "button",
                            actionProps: {
                                onClick: e => {
                                    e.preventDefault();
                                    setDrafts(prev => sortDraftsAlphabetically(prev))
                                },
                            },
                        }}
                    >
                        <FormattedMessage id={"sortAlphabetically"}/>
                    </Button>
                    <Button
                        color={"secondary"}
                        size={"xs"}
                        iconBefore={<Icon icon={PlusIcon}/>}
                        disabled={disabled || !canAdd}
                        buttonAction={{
                            as: "button",
                            actionProps: {
                                onClick: e => {
                                    e.preventDefault();
                                    onAdd();
                                },
                            },
                        }}
                    >
                        <FormattedMessage id={"addOption"}/>
                    </Button>
                </div>
            </div>

            <p className={"text-sm text-gray-500 dark:text-slate-400"}>
                <FormattedMessage id={"optionsInGroup.tip"}/>
            </p>

            {
                drafts.length === 0
                    ? (
                        <p className={"text-sm text-gray-500 dark:text-slate-400"}>
                            <FormattedMessage id={"noOptionsInGroup"}/>
                        </p>
                    )
                    : (

                        <SimpleBar
                            className={twJoin(
                                "w-full max-h-[min(400px,50vh)] rounded-lg border",
                                defaultBorderColor,
                            )}
                            autoHide={false}
                        >
                            <ol className={"w-full divide-y divide-gray-200 dark:divide-slate-700 text-sm"}>
                                {drafts.map((draft: DraftOption, index: number) => {
                                    const others: number = otherGroupCount(draft);

                                    return (
                                        <li key={draft.key} className={"flex flex-row items-center gap-3 py-2 pl-3 pr-2"}>
                                        <span className={"w-5 shrink-0 text-gray-400 tabular-nums"}>
                                            {index + 1}.
                                        </span>

                                            <div className={"min-w-0 flex flex-col gap-1"}>
                                            <span className={"font-medium text-gray-900 dark:text-white truncate"}>
                                                {draftName(draft)}
                                            </span>
                                                <div className={"flex flex-wrap items-center gap-1.5 empty:hidden"}>
                                                    {
                                                        draft.kind === "new" &&
                                                        <Badge size={"xs"} color={"primary"}>
                                                            <FormattedMessage id={"new"}/>
                                                        </Badge>
                                                    }
                                                    {
                                                        draft.kind === "existing" && draft.option.soldout &&
                                                        <Badge size={"xs"} color={"danger"}>
                                                            <FormattedMessage id={"soldOut"}/>
                                                        </Badge>
                                                    }
                                                    {
                                                        others > 0 &&
                                                        <Badge size={"xs"} color={"secondary"}>
                                                            <FormattedMessage id={"alsoInOtherGroups"}
                                                                              values={{count: others}}/>
                                                        </Badge>
                                                    }
                                                </div>
                                            </div>

                                            <div className={"ml-auto flex flex-row items-center gap-3 shrink-0"}>
                                                <DraftPrices draft={draft}/>

                                                <div className={"flex flex-row items-center gap-0.5"}>
                                                    <button
                                                        type={"button"}
                                                        className={iconButtonClassName}
                                                        disabled={disabled || index === 0}
                                                        onClick={() => setDrafts(prev => move(prev, index, -1))}
                                                        title={intl.formatMessage({id: "moveUp"})}
                                                    >
                                                        <ArrowUpIcon className={"size-4"}/>
                                                        <span className={"sr-only"}><FormattedMessage id={"moveUp"}/></span>
                                                    </button>
                                                    <button
                                                        type={"button"}
                                                        className={iconButtonClassName}
                                                        disabled={disabled || index === drafts.length - 1}
                                                        onClick={() => setDrafts(prev => move(prev, index, 1))}
                                                        title={intl.formatMessage({id: "moveDown"})}
                                                    >
                                                        <ArrowDownIcon className={"size-4"}/>
                                                        <span className={"sr-only"}><FormattedMessage
                                                            id={"moveDown"}/></span>
                                                    </button>
                                                    <button
                                                        type={"button"}
                                                        className={twJoin(iconButtonClassName, "hover:text-red-600 dark:hover:text-red-400")}
                                                        disabled={disabled}
                                                        onClick={() => setDrafts(prev => prev.filter(d => d.key !== draft.key))}
                                                        title={intl.formatMessage({id: "delete"})}
                                                    >
                                                        <XMarkIcon className={"size-4"}/>
                                                        <span className={"sr-only"}><FormattedMessage id={"delete"}/></span>
                                                    </button>
                                                </div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ol>
                        </SimpleBar>
                    )
            }
        </section>
    );
};

export default DraftOptionsList;